import React, { useState, useEffect } from "react";
import {
  getAllProgramsViaAdmin,
  getApplicationYears,
} from "../../../utils/Api";

// Fallback if the years endpoint is unreachable: the current year and the four before it,
// computed rather than listed, so the dropdown is never empty and never needs editing.
const fallbackYears = () => {
  const now = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => now - i);
};

const PanelFormListFilters = ({ onFilterChange, resetFilter }) => {
  const [year, setYear] = useState("");
  const [applicationNumber, setApplicationNumber] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [caste, setCaste] = useState("");
  const [applicationStage, setApplicationStage] = useState("");
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [search, setSearch] = useState("");
  const [formStatus, setFormStatus] = useState("");
  const [admissionEligibility, setAdmissionEligibility] = useState("");
  const [years, setYears] = useState(fallbackYears);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const { programs } = await getAllProgramsViaAdmin();
        setPrograms(programs);
      } catch (error) {
        console.error("Failed to fetch programs", error);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const { years: fetched } = await getApplicationYears();
        if (Array.isArray(fetched) && fetched.length) setYears(fetched);
      } catch (error) {
        // Keep the computed fallback rather than emptying the dropdown.
        console.error("Failed to fetch application years", error);
      }
    };
    fetchYears();
  }, []);

  const handleFilterChange = () => {
    onFilterChange({
      year,
      applicationNumber,
      paymentStatus,
      caste,
      applicationStage,
      program: selectedProgram,
      search,
      formStatus,
      admissionEligibility,
    });
  };

  const resetForm = () => {
    setYear("");
    setApplicationNumber("");
    setPaymentStatus("");
    setCaste("");
    setApplicationStage("");
    setSelectedProgram("");
    setSearch("");
    setFormStatus("");
    setAdmissionEligibility("");

    // Pass literal empty values. React state updates are asynchronous, so using the state
    // variables here made the first Reset click resend the old filters.
    resetFilter({
      year: "",
      applicationNumber: "",
      paymentStatus: "",
      caste: "",
      applicationStage: "",
      program: "",
      search: "",
      formStatus: "",
      admissionEligibility: "",
    });
  };

  const casts = ["general", "obc", "sc", "st", "other"];
  const stages = [1, 2, 3];
  const paymentStatuses = ["unpaid", "paid", "partial"];
  const formStatuses = [
    "Pending",
    "Completed By Student",
    "View By Admin",
    "Reply",
    "Success",
    "Rejected",
    "Cancelled",
    "Student Admitted",
  ];

  return (
    <div className="row">
      <div className="form-group col-md-4 mb-3">
        <label htmlFor="applicationYear">Select Year</label>
        <select
          id="applicationYear"
          className="form-control"
          onChange={(e) => setYear(e.target.value)}
          value={year}
        >
          <option value="">Select Year</option>
          {years.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="program">Select Program</label>
        <select
          id="program"
          className="form-control"
          onChange={(e) => setSelectedProgram(e.target.value)}
          value={selectedProgram}
        >
          <option value="">Select Program</option>
          {programs.map((program) => (
            <option key={program._id} value={program.programName}>
              {program.programName}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="applicationSearch">Search by Name</label>
        <input
          id="applicationSearch"
          className="form-control"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="applicationNumber">Application Number</label>
        <input
          id="applicationNumber"
          className="form-control"
          onChange={(e) => setApplicationNumber(e.target.value)}
          value={applicationNumber}
        />
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="formStatus">Application Status</label>
        <select
          id="formStatus"
          className="form-control"
          onChange={(e) => setFormStatus(e.target.value)}
          value={formStatus}
        >
          <option value="">All Application Statuses</option>
          {formStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="admissionEligibility">Eligible for Admission</label>
        <select
          id="admissionEligibility"
          className="form-control"
          onChange={(e) => setAdmissionEligibility(e.target.value)}
          value={admissionEligibility}
        >
          <option value="">All Applications</option>
          <option value="eligible">Eligible</option>
          <option value="ineligible">Not Eligible</option>
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="caste">Select Caste</label>
        <select
          id="caste"
          className="form-control"
          onChange={(e) => setCaste(e.target.value)}
          value={caste}
        >
          <option value="">Select Caste</option>
          {casts.map((option) => (
            <option key={option} value={option}>
              {option.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="paymentStatus">Select Payment Status</label>
        <select
          id="paymentStatus"
          className="form-control"
          onChange={(e) => setPaymentStatus(e.target.value)}
          value={paymentStatus}
        >
          <option value="">Select Payment Status</option>
          {paymentStatuses.map((option) => (
            <option key={option} value={option}>
              {option.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group col-md-4 mb-3">
        <label htmlFor="applicationStage">Select Stage</label>
        <select
          id="applicationStage"
          className="form-control"
          onChange={(e) => setApplicationStage(e.target.value)}
          value={applicationStage}
        >
          <option value="">Select Stage</option>
          {stages.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-4 d-flex justify-content-start align-items-center">
        <button
          type="button"
          onClick={handleFilterChange}
          className="btn btn-primary create-new-button"
        >
          Apply Filters
        </button>
        <button
          type="button"
          onClick={resetForm}
          className="btn ml-4 btn-info create-new-button"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default PanelFormListFilters;
